import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import connect from '../../utils/connect';
import combineActions from '../../utils/combineActions';
import usePrevious from '../../components/hooks/usePrevious';

import sportsApi from '../../utils/sportsApi';
import { confirmed, hidden, underReview } from '../../constants/clientConfig';

import getClientOnboarding from '../../actions/settings/getClientOnboarding';
import { STANDARD_PLACEMENT_EXPERIENCE } from '../../constants/config';
import {
    isOnboardingStructureLocked,
    parseOnboardingConfigStatusId,
} from '../utils/onboardingConfigStatus';

export const Context = React.createContext(null);

const Provider = ({
    children,
    client,
    getClientOnboarding,
    settings: {
        client: {
            onboarding = {
                rightsholders: [],
            },
        },
    },
}) => {
    const { configStatusId, id: clientId } = client;

    const hasStandardPlacements =
        client && client.permissions.includes(STANDARD_PLACEMENT_EXPERIENCE);
    const [isLoading, setLoading] = useState(true);
    const [hasSubmitted, setSubmit] = useState(false);
    const [isRemovingSP, setIsRemovingSP] = useState(false);
    const [configStatus, setConfigStatus] = useState(configStatusId);
    const [canSubmit, setSubmittable] = useState(false);
    const [isPlacementModalOpen, setPlacementModalOpen] = useState(false);
    const [allSponsors, setAllSponsors] = useState([]);
    const [allSponsorsLoading, setAllSponsorsLoading] = useState(false);
    const [rhId, setRhId] = useState(null);
    const [hideInactiveAssets, setHideInactiveAssets] = useState(false);
    const [isDraggableActive, setDraggableActive] = useState(false);
    const [currentSelectedPlacement, setCurrentSelectedPlacement] =
        useState(null);

    const [showDraggableItem, setShowDraggableItem] = useState(true);
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [currentSponsorsLists, setCurrentSponsorsList] = useState([]);
    const [sponsorsToAdd, setSponsorsToAdd] = useState([]);
    const [sponsorsToRemove, setSponsorsToRemove] = useState([]);
    const [selectedSponsors, setSelectedSponsors] = useState([]);
    const [isSelectingAllSponsors, setIsSelectingAllSponsors] = useState(false);

    const { rightsholders } = onboarding;

    const structureLocked = useMemo(
        () => isOnboardingStructureLocked(configStatus),
        [configStatus],
    );
    const canAddPlacementSponsors = useMemo(
        () =>
            !structureLocked ||
            parseOnboardingConfigStatusId(configStatus) === hidden,
        [configStatus, structureLocked],
    );

    const { placements, maxPlacements, maxSponsors, sportId } = useMemo(() => {
        const currentRh = rightsholders.find(r => r.id === rhId);
        const hasCurrentRh = !!currentRh;
        if (!hasCurrentRh)
            return {
                placements: [],
                maxPlacements: null,
                maxSponsors: null,
                sportId: 3,
            };
        return currentRh;
    }, [rhId, rightsholders]);

    const prevRhId = usePrevious(rhId);

    // fetch init info

    useEffect(() => {
        let cancelled = false;

        (async () => {
            setLoading(true);
            try {
                await getClientOnboarding(clientId, hideInactiveAssets);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.log(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [hideInactiveAssets]);

    useEffect(() => {
        (async () => {
            const getClientInfo = async () => {
                try {
                    const { configStatusId } = await sportsApi
                        .url(`/clients/${clientId}`)
                        .get()
                        .json();

                    setSubmit(
                        [underReview, confirmed].includes(
                            parseInt(configStatusId),
                        ),
                    );
                    setConfigStatus(parseInt(configStatusId));
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.log(e);
                }
            };

            const fetchAllSponsors = async () => {
                setAllSponsorsLoading(true);
                try {
                    const allSpEnpoint = '/sponsors?noClientId=true';

                    const allSpResponse = await sportsApi
                        .url(allSpEnpoint)
                        .get()
                        .json();

                    setAllSponsors(allSpResponse);
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.log(e);
                } finally {
                    setAllSponsorsLoading(false);
                }
            };
            await Promise.all([fetchAllSponsors(), getClientInfo()]);
        })();
    }, []);

    // current placement handling

    useEffect(() => {
        if (!rhId) return;
        setCurrentSelectedPlacement(null);
    }, [rhId]);

    useEffect(() => {
        if (currentSelectedPlacement) {
            const placement = placements.find(
                p => p.id === currentSelectedPlacement.id,
            );
            if (placement && prevRhId === rhId)
                setCurrentSelectedPlacement(placement);
        }
    }, [placements]);

    const leagueId = useMemo(() => {
        if (!rhId) return null;
        const rh = rightsholders.find(rh => rh.id === rhId);
        return rh.leagueId ? rh.leagueId : null;
    }, [rightsholders, rhId]);

    const providerValue = {
        isLoading,
        hasSubmitted,
        setSubmit,
        isRemovingSP,
        setIsRemovingSP,
        configStatus,
        setConfigStatus,
        structureLocked,
        canAddPlacementSponsors,
        canSubmit,
        setSubmittable,
        isPlacementModalOpen,
        setPlacementModalOpen,
        allSponsors,
        allSponsorsLoading,
        rhId,
        setRhId,
        hideInactiveAssets,
        setHideInactiveAssets,
        isDraggableActive,
        setDraggableActive,
        currentSelectedPlacement,
        setCurrentSelectedPlacement,
        showDraggableItem,
        setShowDraggableItem,
        isDraggingOver,
        setIsDraggingOver,
        isDragging,
        setIsDragging,
        currentSponsorsLists,
        setCurrentSponsorsList,
        sponsorsToAdd,
        setSponsorsToAdd,
        sponsorsToRemove,
        setSponsorsToRemove,
        selectedSponsors,
        setSelectedSponsors,
        isSelectingAllSponsors,
        setIsSelectingAllSponsors,
        prevRhId,
        placements,
        maxPlacements,
        maxSponsors,
        sportId,
        leagueId,
        hasStandardPlacements,
    };

    return (
        <Context.Provider value={providerValue}>{children}</Context.Provider>
    );
};

Provider.propTypes = {
    children: PropTypes.node.isRequired,
    client: PropTypes.object.isRequired,
    getClientOnboarding: PropTypes.func.isRequired,
    settings: PropTypes.object.isRequired,
};

export default connect(
    ['client', 'user', 'settings'],
    combineActions({
        getClientOnboarding,
    }),
)(Provider);
